import appLogger from '@/utils/logger';
import DOMPurify from 'dompurify';
import { isTranslatedQuoteAttribution, QUOTE_FROM_LABEL, QUOTE_SENT_LABEL_ANYWHERE } from '@/utils/emailBodyDisplay';

// SSR guard - DOMPurify requires a DOM environment
const isBrowser = typeof window !== 'undefined';

/**
 * Sanitizes HTML content to prevent XSS attacks while preserving safe formatting
 * Used for rendering email bodies from database (sent emails and replies)
 */
export function sanitizeEmailHTML(html: string): string {
  if (!html || typeof html !== 'string') {
    return '';
  }

  // During SSR, return empty string or unsanitized html with warning
  // Components using this should have 'use client' directive
  if (!isBrowser) {
    appLogger.warn('sanitizeEmailHTML called during SSR - returning empty string');
    return '';
  }

  return DOMPurify.sanitize(html, {
    // Allow common email formatting tags
    ALLOWED_TAGS: [
      'p', 'br', 'div', 'span', 'strong', 'em', 'u', 'b', 'i',
      'h1', 'h2', 'h3', 'h4', 'h5', 'h6',
      'ul', 'ol', 'li',
      'a', 'blockquote', 'pre', 'code',
      'table', 'thead', 'tbody', 'tr', 'th', 'td'
    ],
    // Allow safe attributes (no 'style' -- prevents CSS-based tracking via background-image URLs)
    ALLOWED_ATTR: ['href', 'target', 'class'],
    // Don't allow data attributes (could contain tracking info)
    ALLOW_DATA_ATTR: false,
    // Keep relative URLs (email links)
    ALLOW_UNKNOWN_PROTOCOLS: false,
    // Force target="_blank" for links
    ADD_ATTR: ['target'],
    // Forbid tags that could execute scripts
    FORBID_TAGS: ['script', 'iframe', 'object', 'embed', 'form', 'input', 'button'],
    // Forbid attributes that could execute scripts
    FORBID_ATTR: ['onerror', 'onload', 'onclick', 'onmouseover']
  });
}

/**
 * Sanitizes signature HTML for in-app preview.
 * Extends sanitizeEmailHTML to allow <img> tags and inline styles — both required
 * for Gmail signatures that use table-based layouts and img icons.
 * Always apply proxySignatureImages() after this so img src goes through the
 * same-origin proxy and img-src 'self' covers it.
 */
export function sanitizeSignatureHTML(html: string): string {
  if (!html || typeof html !== 'string') {
    return '';
  }

  if (!isBrowser) {
    appLogger.warn('sanitizeSignatureHTML called during SSR - returning empty string');
    return '';
  }

  const sanitized = DOMPurify.sanitize(html, {
    ALLOWED_TAGS: [
      'p', 'br', 'div', 'span', 'strong', 'em', 'u', 'b', 'i',
      'h1', 'h2', 'h3', 'h4', 'h5', 'h6',
      'ul', 'ol', 'li',
      'a', 'blockquote', 'pre', 'code',
      'table', 'thead', 'tbody', 'tr', 'th', 'td',
      'img',
    ],
    ALLOWED_ATTR: [
      'href', 'target', 'class', 'style', 'role',
      // img
      'src', 'alt', 'width', 'height',
      // table layout (old-school Gmail signatures)
      'cellpadding', 'cellspacing', 'border', 'valign', 'align',
      // dir (rtl/ltr signatures)
      'dir',
    ],
    ALLOW_DATA_ATTR: false,
    ALLOW_UNKNOWN_PROTOCOLS: false,
    ADD_ATTR: ['target'],
    FORBID_TAGS: ['script', 'iframe', 'object', 'embed', 'form', 'input', 'button'],
    FORBID_ATTR: ['onerror', 'onload', 'onclick', 'onmouseover', 'onfocus', 'onblur'],
  });

  if (!/\srole=/.test(sanitized)) return sanitized;

  const template = document.createElement('template');
  template.innerHTML = sanitized;
  template.content.querySelectorAll('[role]').forEach((element) => {
    const role = element.getAttribute('role');
    if (role !== 'presentation' && role !== 'none') {
      element.removeAttribute('role');
    }
  });
  return template.innerHTML;
}

/**
 * Truncates HTML content to a specific character limit while preserving valid HTML structure
 * Used for email body previews in cards
 */
function truncateHTML(html: string, maxLength: number = 200): string {
  if (!html) return '';

  // First sanitize the HTML
  const sanitized = sanitizeEmailHTML(html);

  // Strip all HTML tags for length calculation
  const textOnly = sanitized.replace(/<[^>]*>/g, '');

  // If already short enough, return as-is
  if (textOnly.length <= maxLength) {
    return sanitized;
  }

  // Truncate the text content
  const truncatedText = textOnly.slice(0, maxLength).trim();

  // Return plain text with ellipsis (simpler and safer than trying to preserve HTML structure)
  return truncatedText + '...';
}

/**
 * Converts HTML to plain text by stripping all tags
 * Used for editing (preserves trailing whitespace) and text-only displays
 */
export function htmlToPlainText(html: string): string {
  if (!html) return '';

  // Sanitize first to be safe
  const sanitized = sanitizeEmailHTML(html);

  // IMPORTANT: Order matters! Handle paragraph breaks BEFORE removing individual tags
  // This prevents adding extra newlines at the end
  // NOTE: No .trim() - preserve leading/trailing whitespace for accurate editing
  return sanitized
    .replace(/<\/p>\s*<p>/gi, '\n\n')    // Convert paragraph BREAKS to double newline FIRST
    .replace(/<\/p>/g, '')                // Remove remaining closing p tags (becomes empty, not \n\n!)
    .replace(/<p>/gi, '')                 // Remove opening p tags
    .replace(/<br\s*\/?>/gi, '\n')        // Convert <br> to newline
    .replace(/<[^>]*>/g, '')              // Remove remaining HTML tags
    .replace(/&nbsp;/g, ' ')              // Non-breaking spaces
    .replace(/&amp;/g, '&')               // Ampersands
    .replace(/&lt;/g, '<')                // Less than
    .replace(/&gt;/g, '>')                // Greater than
    .replace(/&quot;/g, '"')              // Quotes
    .replace(/\\n/g, '\n');               // Convert escape sequences to actual newlines
}

/**
 * Encodes HTML entities to prevent XSS and ensure valid HTML
 * Used when converting plain text to HTML
 */
function encodeHTMLEntities(text: string): string {
  return text
    .replace(/&/g, '&amp;')    // Must be first to avoid double-encoding
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

/**
 * Converts plain text to HTML with paragraph tags
 * Properly encodes HTML entities to prevent XSS and ensure valid HTML
 * Used for converting textarea content to HTML for database storage
 */
export function plainTextToHtml(text: string): string {
  const normalized = text.replace(/\r\n/g, '\n');

  // If the input is only whitespace/newlines, return empty HTML
  if (!normalized.trim()) return '';

  // Split on double newlines but keep empty paragraphs so blank lines are preserved
  const paragraphs = normalized.split(/\n\n/);

  return paragraphs
    .map(p => {
      // Encode HTML entities first to prevent XSS and ensure valid HTML
      const encoded = encodeHTMLEntities(p);
      // Single newlines become <br>, wrap in <p>
      return `<p>${encoded.replace(/\n/g, '<br>')}</p>`;
    })
    .join('');
}

/**
 * Trims leading and trailing empty HTML elements (empty paragraphs, br tags, whitespace)
 * Used for cleaning up AI-generated email content that may have extra whitespace
 */
export function trimHtmlWhitespace(html: string): string {
  if (!html || typeof html !== 'string') {
    return '';
  }

  let trimmed = html;

  // Remove leading empty elements: <p></p>, <p><br></p>, <p>&nbsp;</p>, <p> </p>, standalone <br>
  // Also handles whitespace between tags
  trimmed = trimmed.replace(/^(\s*(<p>(\s|<br\s*\/?>|&nbsp;)*<\/p>|<br\s*\/?>)\s*)+/gi, '');

  // Remove trailing empty elements
  trimmed = trimmed.replace(/(\s*(<p>(\s|<br\s*\/?>|&nbsp;)*<\/p>|<br\s*\/?>)\s*)+$/gi, '');

  // Trim any remaining leading/trailing whitespace
  trimmed = trimmed.trim();

  return trimmed;
}

/**
 * Strips quoted reply history from inbound email HTML.
 * Handles Gmail, Outlook, Apple Mail, and Gong-style quote patterns so each
 * message in the thread shows only its new content.
 *
 * Patterns removed:
 *  - Gmail:       .gmail_quote, .gmail_quote_container, .gmail_attr
 *  - Outlook:     <div> block containing "From: … Sent: … To: … Subject:" headers
 *  - Apple Mail / Gong: "On [date] … wrote:" divider + the <blockquote> that follows
 *  - Bare blockquotes that immediately follow any of the above markers
 */
export function stripGmailQuotes(html: string): string {
  if (!html || typeof html !== 'string') {
    return '';
  }

  if (!isBrowser) {
    return html;
  }

  const parser = new DOMParser();
  const doc = parser.parseFromString(html, 'text/html');
  const body = doc.body;

  // 1. Gmail-specific classes
  body.querySelectorAll('.gmail_quote, .gmail_quote_container').forEach(el => el.remove());
  body.querySelectorAll('.gmail_attr').forEach(el => el.remove());

  // 2. Outlook "From: … Sent: … To: … Subject:" header block.
  // Outlook renders the attribution as a <p> or <div> containing <b>From:</b>,
  // <b>Sent:</b>, <b>To:</b>, <b>Subject:</b> child elements. The key constraint:
  // check shallowText (own direct text + immediate bold children) rather than
  // full textContent so we don't match an ancestor wrapper whose nested content
  // happens to contain these words.
  const outlookFromBoldPattern = QUOTE_FROM_LABEL;
  const outlookFullPattern = QUOTE_SENT_LABEL_ANYWHERE;
  let outlookBoundary: Element | null = null;
  for (const el of Array.from(body.querySelectorAll('p, div'))) {
    // Outlook attribution: first bold/span child text starts with "From:"
    // AND the element's own textContent (not its full subtree) contains "Sent:"
    // Limit textContent check to a reasonable length to avoid matching wrappers
    const ownText = el.textContent || '';
    if (ownText.length > 1000) continue; // skip large wrappers — attribution is short
    const firstBold = el.querySelector('b, strong');
    // The header block itself starts with its From label. A wrapper holding the reply AND the
    // header does not, and taking it as the boundary deleted the reply with the history.
    if (firstBold && outlookFromBoldPattern.test(firstBold.textContent || '') && outlookFromBoldPattern.test(ownText.trim()) && outlookFullPattern.test(ownText)) {
      outlookBoundary = el;
      break;
    }
  }
  if (outlookBoundary) {
    let node: ChildNode | null = outlookBoundary;
    while (node) {
      const next: ChildNode | null = node.nextSibling;
      node.parentNode?.removeChild(node);
      node = next;
    }
  }

  // 3. "On [date] … wrote:" divider (Apple Mail, Gong, and most non-Outlook clients).
  // Match a <div> or <p> whose OWN text (not its full subtree) looks like
  // "On Mon, 23 Apr 2026 at 14:36, Name <email> wrote:". Limit length so we
  // don't accidentally match a wrapper element.
  const onWrotePattern = /^On\s[\s\S]{5,200}wrote:\s*$/i;
  for (const el of Array.from(body.querySelectorAll('div, p'))) {
    const text = (el.textContent || '').trim();
    if (text.length > 300) continue; // skip large containers
    // A translated attribution has no leading anchor word, so it is matched only on an element
    // holding nothing but its own line — text, plus at most the sender's mailto link. A wrapper,
    // or text broken by <br> or split into spans, may carry the reply before the attribution.
    const attributionOnly = Array.from(el.children).every((child) => child.tagName === 'A');
    if (onWrotePattern.test(text) || (attributionOnly && isTranslatedQuoteAttribution(text))) {
      let node: ChildNode | null = el;
      while (node) {
        const next: ChildNode | null = node.nextSibling;
        node.parentNode?.removeChild(node);
        node = next;
      }
      break;
    }
  }

  // 4. Any remaining top-level <blockquote> elements are reply quotes.
  // We only remove direct children of <body> to avoid stripping intentional
  // inline blockquotes that appear before the quote boundary.
  body.querySelectorAll(':scope > blockquote').forEach(el => el.remove());

  return body.innerHTML;
}
